import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LockKey, ArrowRight } from "@phosphor-icons/react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@asymmetryedits.com");
  const [password, setPassword] = useState("Admin@2026");
  const [totp, setTotp] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) nav("/", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email, password, totp);
    setBusy(false);
    if (r?.totp_required) { setNeedsTotp(true); toast.info("Enter your 2FA code"); return; }
    if (r?.ok) { toast.success("Welcome back"); nav("/", { replace: true }); return; }
    if (r?.error) toast.error(r.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-background">
      {/* Editorial visual side */}
      <div className="hidden lg:block relative overflow-hidden bg-foreground">
        <img
          alt=""
          src="https://images.unsplash.com/photo-1571513800374-df1bbe650e56?q=80&w=1600"
          className="absolute inset-0 h-full w-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-foreground/70 via-foreground/40 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 lg:p-16 text-background">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-lg bg-background text-foreground flex items-center justify-center font-display font-bold">Æ</div>
            <div>
              <div className="font-display text-lg font-semibold leading-none">Asymmetry Edits</div>
              <div className="text-[10.5px] uppercase tracking-[0.14em] text-background/70 mt-1">ERP · Employee access</div>
            </div>
          </div>
          <div className="max-w-md">
            <div className="font-display text-5xl xl:text-6xl leading-[1.02] tracking-tight font-bold">Operations, in one calm place.</div>
            <p className="mt-5 text-[15px] text-background/80 leading-relaxed">
              Production, inventory, orders, exchanges, invoices, dispatch, legal, analytics — one interconnected system, private and audited.
            </p>
            <div className="mt-8 text-[11px] uppercase tracking-[0.14em] text-background/60">Version 2026.02</div>
          </div>
        </div>
      </div>

      {/* Auth form side */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-[380px]" data-testid="login-form">
          <div className="lg:hidden mb-8 flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-foreground text-background flex items-center justify-center font-display font-bold">Æ</div>
            <div>
              <div className="font-display text-base font-semibold leading-none">Asymmetry Edits</div>
              <div className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground mt-1">ERP · Employee access</div>
            </div>
          </div>
          <div className="label-eyebrow">Sign in</div>
          <h1 className="display-2 mt-2">Welcome back</h1>
          <p className="text-sm text-muted-foreground mt-2">Use your Asymmetry Edits ERP credentials to continue.</p>

          <div className="space-y-5 mt-8">
            <label className="block">
              <span className="text-[12.5px] font-medium">Email</span>
              <input
                type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="input-soft mt-1.5"
                data-testid="login-email"
              />
            </label>
            <label className="block">
              <span className="text-[12.5px] font-medium">Password</span>
              <input
                type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="input-soft mt-1.5"
                data-testid="login-password"
              />
            </label>
            {needsTotp && (
              <label className="block">
                <span className="text-[12.5px] font-medium">2FA code</span>
                <input
                  inputMode="numeric" required
                  value={totp} onChange={(e) => setTotp(e.target.value)}
                  className="input-soft mt-1.5 tracking-widest font-mono text-center"
                  data-testid="login-totp"
                />
              </label>
            )}
            <button disabled={busy} type="submit" className="btn-primary w-full h-11 group" data-testid="login-submit">
              <LockKey size={15} weight="regular" />
              {busy ? "Signing in…" : "Sign in"}
              {!busy && <ArrowRight size={14} className="ml-auto transition-transform group-hover:translate-x-0.5" />}
            </button>
          </div>

          <div className="mt-10 border-t border-border pt-6 space-y-1 text-xs text-muted-foreground">
            <div>Demo · <span className="font-mono text-foreground/80">admin@asymmetryedits.com / Admin@2026</span></div>
            <div>Additional role logins in <Link to="/" className="underline underline-offset-2 hover:text-foreground">test_credentials.md</Link>.</div>
          </div>
        </form>
      </div>
    </div>
  );
}
